<template>
  <b-col>
    <chromatography-flex-table-title
      :method="data.method"
      :norm="norm"
      :hasAlarms="data.alarmed"
      :executed="data.executed"
    />
    <b-table-simple class="base-gas-ratio-print-table" bordered outlined small caption-top>
      <b-thead>
        <b-tr class="base-gas-ratio-print-table__row-no-border-bottom">
          <b-th>
            <div>{{$t('engineering.reports.test')}}</div>
          </b-th>
          <b-th>
            <div>{{ translate('sample-gas-percentage') }}</div>
          </b-th>
          <b-th class="text-center">
            <div>{{ $t('general.classification') }}</div>
          </b-th>
          <b-th class="text-center">
            <div>{{ $t('engineering.report') }}</div>
          </b-th>
          <b-th v-if="recommendations.length" class="text-center">
            <div>{{ $t('general.recommendations') }}</div>
          </b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="(gasRatio, index) in data.data.ratios"
          :key="index"
          class="base-gas-ratio-print-table__row-no-border-top"
        >
          <b-td class="base-gas-ratio-print-table__column-name">
            <div class="pl-1">
              <gas-label :value="gasRatio.numerator" />/
              <gas-label :value="gasRatio.denominator" />
            </div>
          </b-td>
          <b-td
            class="
              text-center
              base-gas-ratio-print-table__column-value
              "
            >
            <div>{{ $n(gasRatio.result) }}</div>
          </b-td>
          <b-td
            class="base-gas-ratio-print-table__column-classification text-center"
            v-if="!index"
            :rowspan="rowSpanDiagnosticColumn"
          >
            <div class="base-gas-ratio-print-table__wrapper">
              <template v-if="data.data.diagnostics.length">
                <div
                  class="
                    base-gas-ratio-print-table__display-print
                    base-gas-ratio-print-table__bordered-bottom
                  "
                  v-for="(diagnostic, diagnosticIndex) in diagnostics"
                  :key="`${diagnostic.classification}-${diagnosticIndex}`"
                >
                  {{ $t(classificationLabel(diagnostic)) }}
                </div>
              </template>
            </div>
          </b-td>
          <b-td
            class="base-gas-ratio-print-table__column-diagnostic text-center"
            v-if="!index"
            :rowspan="rowSpanDiagnosticColumn"
          >
            <div class="base-gas-ratio-print-table__wrapper">
              <template v-if="diagnostics.length">
                <div
                  class="
                    base-gas-ratio-print-table__display-print
                    base-gas-ratio-print-table__bordered-bottom
                  "
                  v-for="diagnostic in diagnostics"
                  :key="diagnostic.description"
                >
                  {{ diagnostic.description }}
                </div>
              </template>
              <span v-else class="text-green">{{ $t('engineering.normal') }}</span>
            </div>
          </b-td>
          <b-td
            class="base-gas-ratio-print-table__column-recommendations text-center"
            v-if="!index && recommendations.length"
            :rowspan="rowSpanDiagnosticColumn"
          >
            <div class="base-gas-ratio-print-table__wrapper">
              <template>
                <div
                  class="
                    base-gas-ratio-print-table__display-print
                    base-gas-ratio-print-table__bordered-bottom
                  "
                  v-for="(recommendation, recommendationIndex) in recommendations"
                  :key="recommendationIndex"
                >
                  {{ recommendation }}
                </div>
              </template>
            </div>
          </b-td>
        </b-tr>
        <b-tr class="base-gas-ratio-print-table__row-no-border-bottom">
          <b-th
            class="base-gas-ratio-print-table__column-name"
          >{{ $t('engineering.chromatography.methods-and-diagnostics.alarm-levels.gas') }}</b-th>
          <b-th
            class="base-gas-ratio-print-table__column-value text-center"
          >{{ translate('typical-value') }}</b-th>
        </b-tr>
        <b-tr
          v-for="(typicalValue, gas) in data.data.typicalValues"
          :key="gas"
          class="base-gas-ratio-print-table__row-no-border-top"
        >
          <b-td class="base-gas-ratio-print-table__column-name">
            <gas-label
              class="pl-1"
              :value="gas"
            />
          </b-td>
          <b-td
            class="base-gas-ratio-print-table__column-value text-center"
          >{{ $n(typicalValue) }}</b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
  </b-col>
</template>

<script>
import { ClassificationType } from 'sigma-mappings';
import ChromatographyFlexTableTitle from '@/domain/components/installation/engineering/chromatography/methodsAndDiagnostics/individualAnalysis/ChromatographyFlexTableTitle';
import GasLabel from '../../../cardContents/common/GasLabel';

export default {
  name: 'BaseGasRatioPrint',
  props: {
    data: Object,
    norm: String,
  },
  components: {
    ChromatographyFlexTableTitle,
    GasLabel,
  },
  computed: {
    rowSpanDiagnosticColumn() {
      return (
        this.data.data.ratios.length
        + Object.keys(this.data.data.typicalValues).length
        + 1
      );
    },
    diagnostics() {
      return this.data.data.diagnostics || [];
    },
    recommendations() {
      return this.data.data.recommendations || [];
    },
  },
  methods: {
    classificationLabel({ classification }) {
      const classificationTypes = {
        [ClassificationType.ORDINARY]: 'enums.commons.classification-type.ordinary',
        [ClassificationType.URGENT]: 'enums.commons.classification-type.urgent',
        [ClassificationType.CRITICAL]: 'enums.commons.classification-type.critical',
        [ClassificationType.OBSERVATION]: 'enums.commons.classification-type.observation',
      };
      return classificationTypes[classification];
    },
    translate(key) {
      const base = 'engineering.chromatography.methods-and-diagnostics.individual-analysis.print';
      return this.$t(`${base}.${key}`);
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .table-bordered th, .table-bordered td
    border: 1px solid $color-gray-intermediary !important

  .base-gas-ratio-print-table
    ::v-deep tbody th
      background-color: var(--color-table-header-bg) !important
    ::v-deep tbody tr:first-child td
      border-top: none !important
    ::v-deep tbody td
      padding: 0px
    &__wrapper
      display: flex
      flex-direction: column
      position: absolute
      top: 0
      bottom: 0
      left: 0
      right: 0
    &__row
      &-no-border-top td
        border-top: 0 !important
      &-no-border-bottom th
        border-bottom: 0 !important
    &__column-name
      width: 15%
    &__column-value
      width: 20%
    &__column-classification
      width: 10%
      position: relative
      padding: 0
    &__column-diagnostic
      width: auto
      position: relative
      padding: 0
    &__column-recommendations
      width: 20%
      position: relative
      padding: 0
    &__bordered-bottom
      display: flex
      flex: 1
      justify-content: center
      align-items: center
      border-bottom: 1px $color-gray-intermediary solid
      &:last-child
        border-bottom: none

  @media print
    .base-gas-ratio-print-table
      &__display-print
        display: block
        float: none
</style>
